import {useConfig} from '@aboutyou/config';
import {SpotlightSection_Element} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {SlideAmbient, SpotlightSlide} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {CampaignSlide} from './Components/CampaignSlide';
import {ContentSlide} from './Components/ContentSlide';
import {HighTierCouponSlide} from './Components/HighTierCouponSlide';
import {TeaserContent} from './Components/TeaserContent';
import {SlidePropsType} from './Components/types';

export const Slide: FC<SpotlightSection_Element & SlidePropsType> = ({
  type,
  onSlideClick,
  ...slideCommonProps
}) => {
  const {device} = useConfig();
  const isMobile = device === 'mobile';
  const trackOriginClick = useTrackOriginClick();
  const SlideUIComponent = isMobile ? SlideAmbient : SpotlightSlide;

  const handleOnSlideClick =
    (locationId?: string): React.MouseEventHandler =>
    event => {
      onSlideClick?.(event);
      // tracking origin should be location ID, and since this is coming from backend it's not typed in the SDK, therefore is casted here to make typescript happy
      trackOriginClick(locationId as originClick)();
    };

  switch (type?.$case) {
    case 'content':
      return (
        <StyledTrackPromotions
          promotion={{
            source: TrackingPromotionCreative.APIPromotion,
            apiPromotion: {
              ...type.content.promotionTracker,
              destination: type.content.linkTarget.url,
            },
            isSponsored: Boolean(type.content.sponsored),
          }}
          streamPosition={0}
          horizontalPosition={slideCommonProps.index}
          recoList={{
            sectionIndex: 0,
          }}
          triggerOnce
        >
          <ContentSlide
            data-testid="contentSlide"
            content={type.content}
            {...slideCommonProps}
            onSlideClick={handleOnSlideClick(
              type.content.promotionTracker.locationId,
            )}
            SlideUIComponent={SlideUIComponent}
          />
        </StyledTrackPromotions>
      );

    case 'teaser':
      return (
        <StyledTrackPromotions
          promotion={{
            source: TrackingPromotionCreative.APIPromotion,
            apiPromotion: type.teaser.promotionTracker,
          }}
          streamPosition={0}
          horizontalPosition={slideCommonProps.index}
          recoList={{
            sectionIndex: 0,
          }}
          triggerOnce
        >
          <TeaserContent
            data-testid="teaserSlide"
            teaser={type.teaser}
            {...slideCommonProps}
            onSlideClick={handleOnSlideClick(
              type.teaser.promotionTracker.locationId,
            )}
            SlideUIComponent={SlideUIComponent}
          />
        </StyledTrackPromotions>
      );

    case 'campaign':
      return (
        <StyledTrackPromotions
          promotion={{
            source: TrackingPromotionCreative.APIPromotion,
            apiPromotion: type.campaign.promotionTracker,
          }}
          streamPosition={0}
          horizontalPosition={slideCommonProps.index}
          recoList={{
            sectionIndex: 0,
          }}
          triggerOnce
        >
          <CampaignSlide
            data-testid="campaignSlide"
            campaign={type.campaign}
            {...slideCommonProps}
            onSlideClick={handleOnSlideClick(
              type.campaign.promotionTracker.locationId,
            )}
            SlideUIComponent={SlideUIComponent}
          />
        </StyledTrackPromotions>
      );

    case 'highTierCoupon':
      return (
        <StyledTrackPromotions
          promotion={{
            source: TrackingPromotionCreative.APIPromotion,
            apiPromotion: type.highTierCoupon.promotionTracker,
          }}
          streamPosition={0}
          horizontalPosition={slideCommonProps.index}
          recoList={{
            sectionIndex: 0,
          }}
          triggerOnce
        >
          <HighTierCouponSlide
            data-testid="highTierCouponSlide"
            highTierCoupon={type.highTierCoupon}
            {...slideCommonProps}
            onSlideClick={handleOnSlideClick(
              type.highTierCoupon.promotionTracker.locationId,
            )}
            SlideUIComponent={SlideUIComponent}
          />
        </StyledTrackPromotions>
      );

    default:
      return null;
  }
};

const StyledTrackPromotions = styled(TrackPromotions)`
  height: 100%;
`;
